// Inline passwordless sign-in: email → magic link or 6-digit code. Used on its own page and in-context.
import { useState, type FormEvent } from "react";
import { api, ApiError } from "../lib/api";
import { useSession } from "../lib/session";

export function SignInPanel({ reason, next, onDone }: { reason?: string; next?: string; onDone?: () => void }) {
  const { refresh } = useSession();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [stage, setStage] = useState<"email" | "code">("email");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const send = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.requestLogin(email.trim(), next ?? location.pathname + location.search);
      setStage("code");
    } catch (err) {
      setError((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  };
  const verify = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.verify({ email: email.trim(), code: code.trim() });
      await refresh();
      onDone?.();
    } catch (err) {
      setError((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="signin">
      {stage === "email" ? (
        <form onSubmit={send}>
          {reason && <p className="signin-reason">{reason}</p>}
          <label className="dial-label" htmlFor="si-email">Your email</label>
          <div className="inline-form">
            <input id="si-email" type="email" required autoComplete="email" placeholder="you@school.edu.my" value={email} onChange={(e) => setEmail(e.target.value)} />
            <button className="btn btn-primary" disabled={busy}>{busy ? "Sending…" : "Email me a sign-in link"}</button>
          </div>
          <p className="muted small">No password. We'll email a link and a 6-digit code; either works.</p>
        </form>
      ) : (
        <form onSubmit={verify}>
          <p className="signin-reason">Check <b>{email}</b>. Click the link in the email, or type the code here.</p>
          <label className="dial-label" htmlFor="si-code">6-digit code</label>
          <div className="inline-form">
            <input id="si-code" className="code-input" inputMode="numeric" autoComplete="one-time-code" pattern="\d{6}" maxLength={6} required value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} />
            <button className="btn btn-primary" disabled={busy || code.length !== 6}>{busy ? "Checking…" : "Sign in"}</button>
          </div>
          <button type="button" className="btn-link small" onClick={() => setStage("email")}>Use a different email</button>
        </form>
      )}
      {error && <p className="form-error" role="alert">{error}</p>}
    </div>
  );
}
